/**
 * PoC Documents Dialog Component
 * Displays a table of all Plan of Care documents with their statuses
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Stack,
  alpha,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { format } from 'date-fns'
import { DownloadIcon } from 'lucide-react'
import api from '@/services/api'
import { FaFileZipper } from "react-icons/fa6";
export interface PoCDocument {
  id: string
  documentName: string
  poc_name?: string
  status: 'draft' | 'certified' | 'submitted'
  createdOn: string
  authorizationStatus:
  | 'preauthorization-pending'
  | 'submitted-for-preauthorization'
  | 'preauthorization-completed'
  | 'preauthorization-declined'
  authorizationNumber?: string
  preauthorizationDocsUrl?: string
  pdf_url?: string
  is_complete?: boolean
  auth_submit_date?: string
  apiDocument?: any // Store full API document for modal display
  certified_by?: { name?: string; id?: string } // Certifier info
  certified_license_url?: {
    licenseFileUrl?: string
    licenseFileUrl1?: string
    licenseFileUrl2?: string
    licenseFileUrl3?: string
    licenseFileUrl4?: string
    licenseNpi?: string
    licenseNumber?: string
  } // Certified license document
  manager_signature?: string
}

interface PoCDocumentsDialogProps {
  open: boolean
  onClose: () => void
  documents: PoCDocument[]
  loading?: boolean
  onEdit?: (documentId: string) => void
  onView?: (documentId: string) => void
  onDownload?: (documentId: string) => void
  onDownloadPreauth?: (documentId: string) => void
  onSubmitForPreauth?: (documentId: string) => void
  onPreauthCompleted?: (documentId: string) => void
  onPreauthDeclined?: (documentId: string) => void
}

const PoCDocumentsDialog: React.FC<PoCDocumentsDialogProps> = ({
  open,
  onClose,
  documents,
  loading = false,
  onEdit,
  onView,
  onDownload,
  onDownloadPreauth,
  onSubmitForPreauth,
  onPreauthCompleted,
  onPreauthDeclined,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [crertifideDialogOpen, setCrertifideDialogOpen] = useState(false)
  const [certifideDialogData, setCertifideDialogData] = useState<any>(null)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )
  const [authStatusDialogOpen, setAuthStatusDialogOpen] = useState(false)



  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    documentId: string
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedDocumentId(documentId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedDocumentId(null)
  }

  const handleEdit = () => {
    if (selectedDocumentId && onEdit) {
      onEdit(selectedDocumentId)
    }
    handleMenuClose()
  }

  const handleDownload = () => {
    if (selectedDocumentId && onDownload) {
      onDownload(selectedDocumentId)
    }
    handleMenuClose()
  }

  const handleDownloadPdf = (pdfUrl: string, documentName: string) => {
    if (!pdfUrl) return

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = documentName || 'PoC_Document.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getStatusColor = (status: PoCDocument['status']) => {
    switch (status) {
      case 'draft':
        return 'default'
      case 'submitted':
        return 'primary'
      case 'certified':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: PoCDocument['status']) => {
    switch (status) {
      case 'draft':
        return 'Draft'
      case 'submitted':
        return 'Submitted'
      case 'certified':
        return 'Certified'
      default:
        return status
    }
  }

  const getAuthorizationStatusLabel = (
    status: PoCDocument['authorizationStatus']
  ) => {
    switch (status) {
      case 'preauthorization-pending':
        return 'Pre-authorization Pending Submission'
      case 'submitted-for-preauthorization':
        return 'Submitted for Preauthorization'
      case 'preauthorization-completed':
        return 'Preauthorization completed'
      case 'preauthorization-declined':
        return 'Preauthorization Declined'
      default:
        return status
    }
  }

  const getAuthorizationStatusColor = (
    status: PoCDocument['authorizationStatus']
  ) => {
    switch (status) {
      case 'preauthorization-pending':
        return 'warning'
      case 'submitted-for-preauthorization':
        return 'info'
      case 'preauthorization-completed':
        return 'success'
      case 'preauthorization-declined':
        return 'error'
      default:
        return 'default'
    }
  }

  const getAuthorizationStatusIcon = (
    status: PoCDocument['authorizationStatus']
  ) => {
    switch (status) {
      case 'preauthorization-pending':
        return <PendingIcon fontSize="small" />
      case 'submitted-for-preauthorization':
        return <DescriptionIcon fontSize="small" />
      case 'preauthorization-completed':
        return <CheckCircleIcon fontSize="small" />
      case 'preauthorization-declined':
        return <CancelIcon fontSize="small" />
      default:
        return null
    }
  }
  const certifiedNameByname = (certifiedBy: string | undefined) => {
    return `Certified By ${certifiedBy ?? 'Unknown'}`
  }
  const getSelectedDocument = () => {
    return documents.find(d => d.id === selectedDocumentId)
  }


  const handleDownloadZip = async (id: string) => {
    //download zip file
    try {
      const response = await api.get(`/sessions/download-zip/${id}`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = `poc-documents-${id}.zip`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading POC documents:', error)
    }
  }

  const getActionIcon = (document: PoCDocument) => {
    // If work is complete, disable all actions

    if (document.authorizationStatus === 'preauthorization-pending' && document.status !== 'certified') {
      return (
        <Tooltip placement="top" title="Pre-authorization Pending" arrow>
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setAuthStatusDialogOpen(true)
              }}

            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )
    }
    if (document.is_complete) {
      return (
        <Tooltip placement="top" title="More Actions" arrow>
          <span>
            <IconButton
              size="small"
              onClick={e => handleMenuOpen(e, document.id)}

            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )
    }

    // Show eye icon for approved/denied statuses
    if (
      document.authorizationStatus === 'preauthorization-completed' ||
      document.authorizationStatus === 'preauthorization-declined'
    ) {
      return (
        <Tooltip placement="top" title="View Details" arrow>
          <IconButton
            size="small"
            onClick={() => onView && onView(document.id)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: '#2E7DAF',
                backgroundColor: alpha('#2E7DAF', 0.1),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }

    // Disable action icon for draft or submitted status
    if (document.status === 'draft' || document.status === 'submitted') {
      return (
        <Tooltip placement="top" title="More Actions" arrow>
          <span>
            <IconButton
              size="small"
              onClick={e => handleMenuOpen(e, document.id)}

            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )
    }



    // Show three dots menu for certified with pending or submitted-for-preauthorization statuses
    if (
      document.status === 'certified' &&
      (
        document.authorizationStatus === 'submitted-for-preauthorization')
    ) {
      return (
        <Tooltip placement="top" title="More Actions" arrow>
          <IconButton
            size="small"
            onClick={e => handleMenuOpen(e, document.id)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: '#2E7DAF',
                backgroundColor: alpha('#2E7DAF', 0.1),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }


    // Default: show disabled icon
    return (
      <Tooltip placement="top" title="More Actions" arrow>
        <span>
          <IconButton
            size="small"
            onClick={e => handleMenuOpen(e, document.id)}

          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    )
  }



  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: '#2E7DAF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DescriptionIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                PoC Document
              </Typography>
              {/* <Typography variant="caption" color="text.secondary">
                {documents.length} document{documents.length !== 1 ? 's' : ''}{' '}
                found
              </Typography> */}
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'text.primary',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 'calc(90vh - 120px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'grey.100',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'grey.400',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'grey.500',
                  },
                },
              }}
            >
              <Table stickyHeader sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      PoC Document
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      PoC status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      Created on
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      Pre-authorization Status
                    </TableCell>
                    {/* <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                      align="center"
                    >
                      Preauthorization Docs
                    </TableCell> */}
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        py: 2,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Stack spacing={2} alignItems="center">
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '50%',
                              backgroundColor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <DescriptionIcon
                              sx={{ fontSize: 48, color: 'grey.400' }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            No PoC documents found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Generate your first Plan of Care document to get
                            started
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map((document, index) => (
                      <TableRow
                        key={document.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha('#2E7DAF', 0.04),
                          },
                          transition: 'background-color 0.2s ease',
                          borderBottom:
                            index < documents.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <TableCell sx={{ py: 2.5 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                p: 0.75,
                                borderRadius: 1.5,
                                backgroundColor: alpha('#2E7DAF', 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <DescriptionIcon
                                sx={{ fontSize: 20, color: '#2E7DAF' }}
                              />
                            </Box>
                            {document.pdf_url ? (
                              <Typography
                                component="span"
                                variant="body2"
                                fontWeight={500}
                                onClick={() =>
                                  handleDownloadPdf(
                                    document.pdf_url!,
                                    document.poc_name || document.documentName
                                  )
                                }
                                sx={{
                                  color: 'text.primary',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: '#2E7DAF',
                                    textDecoration: 'underline',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {document.poc_name || document.documentName}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: 'text.primary' }}
                              >
                                {document.poc_name || document.documentName}
                              </Typography>
                            )}
                            {document.status === 'draft' &&
                              !document.is_complete && (
                                <Tooltip placement="top" title="Edit Document" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      onEdit && onEdit(document.id)
                                    }
                                    sx={{
                                      color: 'text.secondary',
                                      ml: 0.5,
                                      '&:hover': {
                                        color: '#2E7DAF',
                                        backgroundColor: alpha('#2E7DAF', 0.1),
                                      },
                                      transition: 'all 0.2s ease',
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </TableCell>
                        {document.status === 'certified' ? (
                          <>
                            {document.certified_by?.name ? (
                              <TableCell sx={{ py: 2.5 }}>

                                <Chip
                                  label={certifiedNameByname(document.certified_by?.name)}
                                  size="small"
                                  onClick={() => { setCrertifideDialogOpen(true); setCertifideDialogData(document) }}
                                  sx={{
                                    backgroundColor: alpha('#4caf50', 0.15),
                                    color: '#4caf50',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    '& .MuiChip-label': {
                                      px: 1.5,
                                    },
                                  }}
                                />

                              </TableCell>
                            ) : (
                              <TableCell sx={{ py: 2.5 }}>

                                <Chip
                                  label={"Not Certified"}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha('#4caf50', 0.15),
                                    color: '#4caf50',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    '& .MuiChip-label': {
                                      px: 1.5,
                                    },
                                  }}
                                />
                              </TableCell>
                            )}

                          </>
                        ) : (
                          <TableCell sx={{ py: 2.5 }}>
                            <Chip
                              label={getStatusLabel(document.status)}
                              size="small"
                              sx={{
                                backgroundColor:
                                  document.status === 'draft'
                                    ? alpha('#9e9e9e', 0.2)
                                    : document.status === 'submitted'
                                      ? alpha('#2E7DAF', 0.2)
                                      : alpha('#4caf50', 0.15),
                                color:
                                  document.status === 'draft'
                                    ? '#616161'
                                    : document.status === 'submitted'
                                      ? '#2E7DAF'
                                      : '#4caf50',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1.5,
                                },
                              }}
                            />
                            {document.authorizationStatus === 'preauthorization-pending' && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setAuthStatusDialogOpen(true)
                                }}
                                sx={{ ml: 0.5, p: 0.25 }}
                              >
                                <InfoIcon color="primary" fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        )}

                        <TableCell sx={{ py: 2.5 }}>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              color="text.primary"
                              fontWeight={500}
                            >
                              {format(
                                new Date(document.createdOn),
                                'MM/dd/yyyy'
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {format(new Date(document.createdOn), 'hh:mm a')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Stack spacing={0.5}>
                            <Box display="flex" alignItems="center">
                              <Chip
                                icon={getAuthorizationStatusIcon(
                                  document.authorizationStatus
                                )}
                                label={getAuthorizationStatusLabel(
                                  document.authorizationStatus
                                )}
                                color={getAuthorizationStatusColor(
                                  document.authorizationStatus
                                )}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  height: 26,
                                  borderWidth: 1.5,
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                  },
                                }}
                              />
                              {/* infrmation icon onnly priautrize pending */}

                            </Box>
                            {document.authorizationNumber && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: 'success.main',
                                  fontWeight: 600,
                                  mt: 0.5,
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                                Authorization Number:{' '}
                                {document.authorizationNumber}
                              </Typography>
                            )}
                            {document.authorizationStatus ===
                              'submitted-for-preauthorization' &&
                              document.auth_submit_date && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: 'info.main',
                                    fontWeight: 500,
                                    mt: 0.5,
                                  }}
                                >
                                  <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                  Submission Date:{' '}
                                  {format(
                                    new Date(document.auth_submit_date),
                                    'MM/dd/yyyy'
                                  )}{' '}
                                  {/* {format(
                                    new Date(document.auth_submit_date),
                                    'hh:mm a'
                                  )} */}
                                </Typography>
                              )}
                          </Stack>
                        </TableCell>
                        {/* <TableCell sx={{ py: 2.5 }} align="center">
                          <Tooltip placement="top" title="Download Preauthorization Documents"
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                onDownloadPreauth &&
                                onDownloadPreauth(document.id)
                              }
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: '#2E7DAF',
                                  backgroundColor: alpha('#2E7DAF', 0.1),
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell> */}
                        <TableCell sx={{ py: 2.5 }}>

                          {getActionIcon(document)}

                          {/* show download icon if status is certified */}
                          {document.manager_signature && (
                            <Tooltip placement="top" title="Pre-authorization Documents" arrow>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDownloadZip(document.id)
                                }
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: '#2E7DAF',
                                    backgroundColor: alpha('#2E7DAF', 0.1),
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {/* <DownloadIcon fontSize="small" /> */}
                                <FaFileZipper color='red' fontSize={22} />
                              </IconButton>
                            </Tooltip>
                          )}

                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        {crertifideDialogOpen && (
          <Dialog
            open={crertifideDialogOpen}
            onClose={() => setCrertifideDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                overflow: 'hidden',
              },
            }}
          >
            {/* Dialog Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #2E7DAF 0%, #1a5f8a 100%)',
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 22 }} />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                  Certificate Details
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => setCrertifideDialogOpen(false)}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.15)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>



            <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>


              {/* Certified By info */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha('#4caf50', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#4caf50', 0.25),
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '50%',
                    backgroundColor: alpha('#4caf50', 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                </Box>
                <Box>

                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Certified By
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: '#1a1a1a', lineHeight: 1.3 }}>
                    {certifideDialogData?.certified_by?.name || '—'}
                  </Typography>
                </Box>
              </Box>
              {/* other details box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: alpha('#2E7DAF', 0.04),
                  border: '1px solid',
                  borderColor: alpha('#2E7DAF', 0.15),
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: '50%',
                      backgroundColor: alpha('#2E7DAF', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionIcon sx={{ color: '#2E7DAF', fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                    License Details
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: alpha('#2E7DAF', 0.1), mb: 2 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.5 }}>
                      License NPI
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>
                      {certifideDialogData?.certified_license_url?.licenseNpi || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 0.5 }}>
                      License Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>
                      {certifideDialogData?.certified_license_url?.licenseNumber || '—'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Download Licenses Grid */}
              {certifideDialogData?.certified_license_url && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, px: 0.5 }}>
                    Download Licenses
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: 2,
                    }}
                  >
                    {[
                      { url: certifideDialogData.certified_license_url?.licenseFileUrl, label: 'License 1' },
                      { url: certifideDialogData.certified_license_url?.licenseFileUrl1, label: 'License 2' },
                      { url: certifideDialogData.certified_license_url?.licenseFileUrl2, label: 'License 3' },
                      { url: certifideDialogData.certified_license_url?.licenseFileUrl3, label: 'License 4' },
                      { url: certifideDialogData.certified_license_url?.licenseFileUrl4, label: 'License 5' },
                    ]
                      .filter(item => item.url)
                      .map((license, idx) => (
                        <Box
                          key={idx}
                          onClick={() => {
                            handleDownloadPdf(
                              license.url!,
                              certifideDialogData.certified_by?.name || certifideDialogData.poc_name || certifideDialogData.documentName
                            )
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: alpha('#2E7DAF', 0.12),
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#2E7DAF',
                              backgroundColor: alpha('#2E7DAF', 0.02),
                              boxShadow: '0 4px 12px rgba(46,125,175,0.1)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: alpha('#2E7DAF', 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <DescriptionIcon sx={{ color: '#2E7DAF', fontSize: 20 }} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#334155' }}>
                              {license.label}
                            </Typography>
                          </Stack>
                          <Box sx={{ color: '#2E7DAF', display: 'flex', alignItems: 'center' }}>
                            <DownloadIcon size={18} />
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setCrertifideDialogOpen(false)}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&:hover': { borderColor: '#2E7DAF', color: '#2E7DAF', backgroundColor: alpha('#2E7DAF', 0.05) },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Authorization Status Info Dialog */}
        <Dialog
          open={authStatusDialogOpen}
          onClose={() => setAuthStatusDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            },
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #2E7DAF 0%, #1a5f8a 100%)',
              px: 3,
              py: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InfoIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                Authorization Status
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={() => setAuthStatusDialogOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.15)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <DialogContent sx={{ px: 3, pt: 4, pb: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1.1rem', fontWeight: 500 }}>
              Your request is under review by a certified manager. Please wait.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setAuthStatusDialogOpen(false)}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #2E7DAF 0%, #1a5f8a 100%)',
                boxShadow: '0 2px 8px rgba(46,125,175,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a5f8a 0%, #134a6e 100%)',
                  boxShadow: '0 4px 14px rgba(46,125,175,0.45)',
                },
              }}
            >
              Okay
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            minWidth: 220,
          },
        }}
      >
        {(() => {
          const selectedDoc = getSelectedDocument()
          if (!selectedDoc) return null

          const menuItems: React.ReactNode[] = []

          if (selectedDoc.status === 'draft') {
            menuItems.push(
              <MenuItem
                key="edit-draft"
                onClick={() => {
                  if (selectedDocumentId && onEdit) {
                    onEdit(selectedDocumentId)
                  }
                  handleMenuClose()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha('#2E7DAF', 0.1),
                  },
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#2E7DAF' }} />
                <Typography variant="body2" fontWeight={500}>
                  Edit Draft
                </Typography>
              </MenuItem>
            )
          }

          if (selectedDoc.authorizationStatus === 'preauthorization-pending') {
            menuItems.push(
              <MenuItem
                key="submit-preauth"
                onClick={() => {
                  if (selectedDocumentId && onSubmitForPreauth) {
                    onSubmitForPreauth(selectedDocumentId)
                  }
                  handleMenuClose()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha('#2E7DAF', 0.1),
                  },
                }}
              >
                <DescriptionIcon
                  fontSize="small"
                  sx={{ mr: 1.5, color: '#2E7DAF' }}
                />
                <Typography variant="body2" fontWeight={500}>
                  Submit for Preauthorization
                </Typography>
              </MenuItem>
            )
          }

          if (selectedDoc.authorizationStatus === 'submitted-for-preauthorization') {
            menuItems.push(
              <MenuItem
                key="preauth-completed"
                onClick={() => {
                  if (selectedDocumentId && onPreauthCompleted) {
                    onPreauthCompleted(selectedDocumentId)
                  }
                  handleMenuClose()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha('#4caf50', 0.1),
                  },
                }}
              >
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ mr: 1.5, color: '#4caf50' }}
                />
                <Typography variant="body2" fontWeight={500}>
                  Preauthorization Completed
                </Typography>
              </MenuItem>
            )
            menuItems.push(
              <MenuItem
                key="preauth-declined"
                onClick={() => {
                  if (selectedDocumentId && onPreauthDeclined) {
                    onPreauthDeclined(selectedDocumentId)
                  }
                  handleMenuClose()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha('#f44336', 0.1),
                  },
                }}
              >
                <CancelIcon
                  fontSize="small"
                  sx={{ mr: 1.5, color: '#f44336' }}
                />
                <Typography variant="body2" fontWeight={500}>
                  Preauthorization Declined
                </Typography>
              </MenuItem>
            )
          }

          if (
            selectedDoc.authorizationStatus === 'preauthorization-completed' ||
            selectedDoc.authorizationStatus === 'preauthorization-declined'
          ) {
            menuItems.push(
              <MenuItem
                key="view-details"
                onClick={() => {
                  if (selectedDocumentId && onView) {
                    onView(selectedDocumentId)
                  }
                  handleMenuClose()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha('#2E7DAF', 0.1),
                  },
                }}
              >
                <VisibilityIcon
                  fontSize="small"
                  sx={{ mr: 1.5, color: '#2E7DAF' }}
                />
                <Typography variant="body2" fontWeight={500}>
                  View Details
                </Typography>
              </MenuItem>
            )
          }

          if (menuItems.length === 0) {
            return (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No actions available
                </Typography>
              </MenuItem>
            )
          }

          return <>{menuItems}</>
        })()}
      </Menu>
    </>
  )
}

export default PoCDocumentsDialog
